import axios from 'axios'

/**
 * Browser producer for the Source Tracking v1 contract (TRDEV-33311).
 *
 * Builds the Pinpoint-compatible envelope collected by applicant-bff at
 * `POST /source-tracking/events` and delivers it asynchronously. Delivery never throws and never
 * blocks the caller; a bounded retry reuses the same `migration_event_id` so retries are
 * idempotent downstream. This file is shared verbatim between applicant-ui and
 * social-recruiting-applicant-ui — keep them in sync.
 */

export const CONTRACT_VERSION = '1'
export const EVENT_VERSION = '3.1'
export const SDK = { name: 'source-tracking-web', version: '1' }
export const COLLECTOR_PATH = '/source-tracking/events'

/**
 * Dedicated client: the default axios instance carries the app-wide 401 interceptor that redirects
 * to the CAS login (src/config/axios.js) and axios-retry. A tracking failure must never do that.
 */
const collectorClient = axios.create()

const RETRYABLE_STATUSES = [408, 425, 429, 500, 502, 503, 504]
const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_BACKOFF_MILLIS = 500

export function newId() {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Pinpoint attributes are flat strings; drop nothing, coerce everything, mirror `undefined` to ''. */
export function toStringAttributes(attributes = {}) {
  return Object.entries(attributes).reduce((acc, [key, value]) => {
    acc[key] = value === undefined || value === null ? '' : String(value)
    return acc
  }, {})
}

function localeParts() {
  const raw = (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
  const [language = 'en', country = ''] = raw.split(/[-_]/)
  return { code: country ? `${language}_${country}` : language, country, language }
}

/**
 * @param producer  { environment, appId, title, packageName, clientId, sessionId }
 * @param event     { name, attributes, metrics, sessionId, migrationEventId, timestamp }
 */
export function buildEvent(producer, event) {
  const now = event.timestamp || Date.now()
  return {
    source_tracking_contract_version: CONTRACT_VERSION,
    migration_event_id: event.migrationEventId || newId(),
    environment: producer.environment,
    event_type: event.name,
    event_timestamp: now,
    event_version: EVENT_VERSION,
    application: {
      app_id: producer.appId,
      package_name: producer.packageName,
      title: producer.title,
      version_name: '',
      version_code: '1',
      sdk: SDK,
    },
    client: { client_id: producer.clientId },
    device: {
      locale: localeParts(),
      make: '',
      model: '',
      platform: { name: 'web', version: '' },
    },
    session: {
      session_id: event.sessionId || producer.sessionId || newId(),
      start_timestamp: now,
      stop_timestamp: null,
    },
    attributes: toStringAttributes(event.attributes),
    metrics: event.metrics || {},
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function isRetryable(error) {
  const status = error && error.response && error.response.status
  return !status || RETRYABLE_STATUSES.includes(status)
}

/**
 * The default XHR adapter cancels in-flight requests when the page unloads (e.g. the applicant
 * clicks Apply right after the posting loads). The fetch adapter with `keepalive` lets the browser
 * finish the request.
 */
const REQUEST_CONFIG = Object.freeze({
  adapter: 'fetch',
  fetchOptions: { keepalive: true },
  headers: { 'Content-Type': 'application/json' },
})

/**
 * POST the built envelope to the collector. Resolves to true/false and never rejects.
 * The same serialized event (same migration_event_id) is re-sent on each attempt.
 */
export async function deliver(bffURL, envelope, options = {}) {
  const maxAttempts = options.maxAttempts || DEFAULT_MAX_ATTEMPTS
  const backoff =
    options.backoffMillis === undefined ? DEFAULT_BACKOFF_MILLIS : options.backoffMillis
  const url = `${bffURL}${COLLECTOR_PATH}`
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await collectorClient.post(url, envelope, REQUEST_CONFIG)
      return true
    } catch (error) {
      if (attempt >= maxAttempts || !isRetryable(error)) {
        console.error(
          'Source tracking delivery failed',
          envelope.migration_event_id,
          error && error.message
        )
        return false
      }
      await sleep(backoff * attempt)
    }
  }
  return false
}

/**
 * Fire-and-forget helper used by producers: builds and delivers on the next tick so the
 * application flow is never blocked or failed by analytics.
 */
export function track(producer, bffURL, event, options) {
  const envelope = buildEvent(producer, event)
  const pending = Promise.resolve()
    .then(() => deliver(bffURL, envelope, options))
    .catch(() => false)
  return { migrationEventId: envelope.migration_event_id, pending }
}
